import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { WhatsNewApi } from '../api/WhatsNewApi'
import type { ProductUpdate, RoadmapItem, WhatsNewLoadState } from '../models/whatsNew.types'
import { hasUnseenUpdates, shouldRefetch, sortUpdates } from '../service/whatsNew'

export const useWhatsNewStore = defineStore(
  'whatsNewStore',
  () => {
    const api = new WhatsNewApi()
    const updates = ref<ProductUpdate[]>([])
    const roadmap = ref<RoadmapItem[]>([])
    const whatsNewLastSeenAt = ref<number | null>(null)
    const updatesFetchedAt = ref<number | null>(null)
    const loadingUpdates = ref(false)
    const loadingRoadmap = ref(false)
    const updatesError = ref(false)
    const roadmapError = ref(false)
    const roadmapFetchedAt = ref<number | null>(null)

    const sortedUpdates = computed(() => sortUpdates(updates.value))
    const hasUnseen = computed(() =>
      hasUnseenUpdates(sortedUpdates.value, whatsNewLastSeenAt.value),
    )
    const state = computed<WhatsNewLoadState>(() => {
      const hasContent = updates.value.length > 0 || roadmap.value.length > 0
      if (hasContent) return 'ready'
      if (loadingUpdates.value || loadingRoadmap.value) return 'loading'
      if (updatesError.value || roadmapError.value) return 'error'
      if (updatesFetchedAt.value !== null && roadmapFetchedAt.value !== null) return 'empty'
      return 'idle'
    })

    async function loadUpdates(force = false): Promise<void> {
      if (loadingUpdates.value || (!force && !shouldRefetch(updatesFetchedAt.value))) return
      loadingUpdates.value = true
      updatesError.value = false
      try {
        updates.value = sortUpdates(await api.getUpdates())
        updatesFetchedAt.value = Date.now()
      } catch {
        updatesError.value = true
        throw new Error('whats-new-updates-failed')
      } finally {
        loadingUpdates.value = false
      }
    }

    async function loadRoadmap(force = false): Promise<void> {
      if (loadingRoadmap.value || (!force && !shouldRefetch(roadmapFetchedAt.value))) return
      loadingRoadmap.value = true
      roadmapError.value = false
      try {
        roadmap.value = await api.getRoadmap()
        roadmapFetchedAt.value = Date.now()
      } catch {
        roadmapError.value = true
        throw new Error('whats-new-roadmap-failed')
      } finally {
        loadingRoadmap.value = false
      }
    }

    async function loadContent(force = false): Promise<void> {
      await Promise.allSettled([loadUpdates(force), loadRoadmap(force)])
    }

    async function toggleVote(
      accountId: number,
      workspaceId: number,
      itemId: number,
      removing: boolean,
    ): Promise<void> {
      const item = roadmap.value.find(({ id }) => id === itemId)
      if (!item) return
      item.votes = Math.max(0, item.votes + (removing ? -1 : 1))
      try {
        await api.toggleRoadmapVote(accountId, workspaceId, itemId)
      } catch (error) {
        item.votes = Math.max(0, item.votes + (removing ? 1 : -1))
        throw error
      }
    }

    function markSeen(): void {
      const newest = sortedUpdates.value[0]
      whatsNewLastSeenAt.value = newest
        ? Date.parse(newest.published_at || newest.created_at)
        : Date.now()
    }

    function resetContent(): void {
      updates.value = []
      roadmap.value = []
      updatesFetchedAt.value = null
      roadmapFetchedAt.value = null
      loadingUpdates.value = false
      loadingRoadmap.value = false
      updatesError.value = false
      roadmapError.value = false
    }

    return {
      updates,
      roadmap,
      whatsNewLastSeenAt,
      updatesFetchedAt,
      roadmapFetchedAt,
      loadingUpdates,
      loadingRoadmap,
      updatesError,
      roadmapError,
      sortedUpdates,
      hasUnseen,
      state,
      loadUpdates,
      loadRoadmap,
      loadContent,
      toggleVote,
      markSeen,
      resetContent,
    }
  },
  {
    persist: {
      key: 'whatsNewStore',
      // Content persists too (not just the seen-cursor): the 1-hour throttle in `loadUpdates`/
      // `loadRoadmap` is otherwise pointless across a hard reload, since `updatesFetchedAt`/
      // `roadmapFetchedAt` would reset to null and every reload would refetch regardless of the
      // TTL (legacy parity — `layoutStore.ts` persisted the whole store; amended 2026-09-09,
      // contract 29 §6.5/§16 updated to match).
      pick: ['whatsNewLastSeenAt', 'updates', 'roadmap', 'updatesFetchedAt', 'roadmapFetchedAt'],
    },
  },
)

export type WhatsNewStore = ReturnType<typeof useWhatsNewStore>
